import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { UserRole } from '@streamr/tv-engine';
import { LargeMicrophoneIcon, rotateSize, TextInput, useOrientToScreenRotationOffset, } from '@streamr/tv-ui';
import { executeSoon } from '@streamr/tv-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { StreamrAppService } from '../../services/streamrapp/StreamrAppService';
import { UserService } from '../../services/user/UserService';
import { VideoPropertiesService } from '../../services/videoProperties/VideoPropertiesService';
import { Popup, TopCenter } from '../../utils/popups';
import { Button } from '../Button';
import { AbstractPopup, DismissalReason } from './AbstractPopup';
import { ScrollablePopupContainer } from './helpers/ScrollablePopupContainer';
import { PopupCssPreset } from './Popup.styles';
function RequestSpeakerRolePopup({ anchorRef, initialName = '', hostUserId, requestedRole = UserRole.SPEAKER, isCallMode = false, reject, resolve, }) {
    const localDisplayName = UserService.getOutput().useLocalDisplayName();
    const [name, setName] = useState(initialName || localDisplayName);
    const [message, setMessage] = useState('');
    const canSubmit = name.trim().length > 0;
    const userService = UserService.getNotificationInterface();
    const playbackAudioMuted = !!StreamrAppService.getOutput().useAudioMuted();
    const videoProperties = VideoPropertiesService.getOutput().useVideoProperties();
    const [uprightVideoWidth, uprightVideoHeight] = rotateSize(videoProperties?.width ?? 1920, videoProperties?.height ?? 1080, videoProperties?.rotation ?? 0);
    const rotationOffset = useOrientToScreenRotationOffset({
        uprightVideoWidth,
        uprightVideoHeight,
        orientToScreen: true,
    });
    const isRotated = rotationOffset !== 0;
    const contentRef = useRef(null);
    const cleanupRef = useRef(null);
    const isRotatedRef = useRef(isRotated);
    isRotatedRef.current = isRotated;
    useEffect(() => () => {
        cleanupRef.current?.();
    }, []);
    const contentCallbackRef = useCallback((contentEl) => {
        cleanupRef.current?.();
        cleanupRef.current = null;
        contentRef.current = contentEl;
        if (!contentEl) {
            return;
        }
        let backdrop = null;
        {
            let el = contentEl;
            while (el) {
                if (getComputedStyle(el).position === 'fixed') {
                    backdrop = el;
                    break;
                }
                el = el.parentElement;
            }
        }
        if (!backdrop) {
            return;
        }
        const backdropEl = backdrop;
        const vars = [
            '--Popup_Y',
            '--Popup_MaxHeight',
            '--Popup_X',
            '--Popup_Padding',
            '--vh-100',
        ];
        let scrollTimeoutId;
        let scrollRafId;
        function update() {
            if (isRotatedRef.current) {
                return;
            }
            const vv = window.visualViewport;
            if (!vv) {
                return;
            }
            const isLandscapeMobile = vv.width > 480 && vv.height < 416;
            if (!isLandscapeMobile) {
                for (const v of vars) {
                    backdropEl.style.removeProperty(v);
                }
                return;
            }
            const center = vv.offsetTop + vv.height / 2;
            backdropEl.style.setProperty('--Popup_Y', `calc(${center}px - 50%)`);
            backdropEl.style.setProperty('--Popup_MaxHeight', `${vv.height - 8}px`);
            backdropEl.style.setProperty('--Popup_X', `calc(${vv.width / 2}px - 50%)`);
            backdropEl.style.setProperty('--Popup_Padding', '0');
            backdropEl.style.setProperty('--vh-100', `${window.innerHeight}px`);
            scheduleScrollToFocused();
        }
        function tryScrollToFocused(retriesLeft) {
            scrollRafId = requestAnimationFrame(() => {
                const focused = document.activeElement;
                if (!(focused instanceof HTMLElement) ||
                    !contentRef.current?.contains(focused)) {
                    return;
                }
                const scrollable = contentRef.current?.closest('[data-overlayscrollbars-viewport]');
                if (!scrollable) {
                    return;
                }
                const sRect = scrollable.getBoundingClientRect();
                const fRect = focused.getBoundingClientRect();
                if (fRect.top >= sRect.top &&
                    fRect.bottom <= sRect.bottom) {
                    return;
                }
                if (scrollable.scrollHeight <= scrollable.clientHeight) {
                    if (retriesLeft > 0) {
                        scrollTimeoutId = window.setTimeout(() => tryScrollToFocused(retriesLeft - 1), 80);
                    }
                    return;
                }
                const targetTop = fRect.top -
                    sRect.top +
                    scrollable.scrollTop -
                    sRect.height / 2 +
                    fRect.height / 2;
                scrollable.scrollTo({
                    top: Math.max(0, targetTop),
                    behavior: 'smooth',
                });
            });
        }
        function scheduleScrollToFocused() {
            clearTimeout(scrollTimeoutId);
            if (scrollRafId != null) {
                cancelAnimationFrame(scrollRafId);
            }
            scrollTimeoutId = window.setTimeout(() => tryScrollToFocused(8), 300);
        }
        contentEl.addEventListener('focusin', scheduleScrollToFocused);
        window.visualViewport?.addEventListener('resize', update);
        window.visualViewport?.addEventListener('scroll', update);
        update();
        cleanupRef.current = () => {
            clearTimeout(scrollTimeoutId);
            if (scrollRafId != null) {
                cancelAnimationFrame(scrollRafId);
            }
            contentEl.removeEventListener('focusin', scheduleScrollToFocused);
            window.visualViewport?.removeEventListener('resize', update);
            window.visualViewport?.removeEventListener('scroll', update);
            for (const v of vars) {
                backdropEl.style.removeProperty(v);
            }
        };
    }, []);
    const popupContent = (_jsx(ScrollablePopupContainer, { fullscreenOnly: isRotated, onCloseButtonClick: () => {
            reject(DismissalReason.CloseButton);
        }, children: _jsxs(Content, { ref: contentCallbackRef, "$rotated": isRotated, children: [_jsx(IconContainer, { "$rotated": isRotated, children: _jsx(LargeMicrophoneIcon, { size: "x3" }) }), _jsx(Title, { "$rotated": isRotated, children: requestedRole === UserRole.GUEST
                        ? 'Request to speak'
                        : 'Request to join' }), _jsx(Subtitle, { "$rotated": isRotated, children: "Others will see your name and video." }), _jsx(NameInput, { placeholder: "Your name", value: name, onChange: (e) => setName(e.target.value), "$rotated": isRotated }), !isCallMode && (_jsx(MessageInput, { placeholder: "Send a message", value: message, onChange: (e) => setMessage(e.target.value), "$rotated": isRotated })), _jsx(SendButton, { type: "button", disabled: !canSubmit, "$rotated": isRotated, onClick: () => {
                        if (playbackAudioMuted) {
                            StreamrAppService.getNotificationInterface().notifyToggleAudio();
                        }
                        const trimmedName = name.trim();
                        if (trimmedName) {
                            StreamrAppService.getNotificationInterface().notifySetDisplayName(trimmedName);
                        }
                        userService.notifyRequestPermission(requestedRole);
                        const trimmedMessage = isCallMode ? '' : message.trim();
                        if (trimmedMessage) {
                            userService.notifySendPrivateMessage(hostUserId, trimmedMessage);
                        }
                        resolve({
                            name: trimmedName,
                            message: trimmedMessage,
                        });
                    }, children: "Send Request" }), _jsx(CancelButton, { type: "button", kind: "secondary", "$rotated": isRotated, onClick: () => {
                        reject();
                    }, children: "Cancel" })] }) }));
    return (_jsx(RequestSpeakerRolePopupRoot, { reject: reject, anchorRef: anchorRef, translateFn: TopCenter, "$rotated": isRotated, children: isRotated ? (_jsx(OrientedContent, { "$angle": rotationOffset, children: _jsx(RotatedCard, { className: "tv-ui-shiny-border", children: popupContent }) })) : (popupContent) }));
}
const OrientedContent = styled.div `
    position: absolute;
    top: 50%;
    left: 50%;
    width: calc(var(--vh, 1vh) * 100);
    height: calc(var(--vw, 1vw) * 100);
    transform: translate(-50%, -50%) rotate(${({ $angle }) => $angle}deg);
    display: flex;
    justify-content: center;
    align-items: center;
`;
const RotatedCard = styled.div `
    --tv-ui-shiny-border-radius: var(--tv-ui-border-radius-2);

    backdrop-filter: var(--tv-ui-glass-backdrop-filter);
    -webkit-backdrop-filter: var(--tv-ui-glass-backdrop-filter);
    background: var(--tv-ui-glass-background);
    color: var(--tv-ui-glass-text-color);
    border-radius: var(--tv-ui-border-radius-2);
    width: 23.75rem;
    max-width: calc(100% - 2rem);
    max-height: calc(100% - 2rem);
    overflow: hidden;
    box-sizing: border-box;

    [data-overlayscrollbars-viewport] > * {
        height: auto;
        padding: 0 1rem;
        grid-template-rows: 0 max-content 0;
        gap: 0;
    }

    [data-overlayscrollbars-viewport] > * > :first-child > :first-child {
        height: 0;
    }

    [data-overlayscrollbars-viewport] > * > :first-child > :last-child {
        display: none;
    }
`;
const RequestSpeakerRolePopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    ${({ $rotated }) => $rotated
    ? css `
                  --Popup_BackdropFilter: none;
                  --Popup_Background: transparent;
                  --Popup_BoxShadow: none;
                  --Popup_Border: 0;
              `
    : css `
                  @media (min-width: 30rem) {
                      ${PopupCssPreset.AnchoredTopCenter};
                      --Popup_Padding: 1rem 0;
                      --Popup_Width: 23.75rem;
                  }

                  @media (min-width: 30rem) and (max-height: 26rem) {
                      --Popup_Padding: 0.5rem 0;
                  }
              `}
`;
const compactCss = css `
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
`;
const Content = styled.div `
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;

    ${({ $rotated }) => $rotated
    ? css `
                  @media (max-width: 26rem) {
                      ${compactCss};
                  }
              `
    : css `
                  @media (max-height: 26rem) {
                      ${compactCss};
                  }
              `}
`;
const IconContainer = styled.div `
    ${({ $rotated }) => $rotated
    ? css `
                  @media (max-width: 26rem) {
                      display: none;
                  }
              `
    : css `
                  @media (max-height: 26rem) {
                      display: none;
                  }
              `}
`;
const compactTitleCss = css `
    font-size: 1.125rem;
    line-height: 1.25em;
`;
const Title = styled.h2 `
    font-size: 1.375rem;
    font-weight: 600;
    line-height: 1.5em;
    color: #ffffff;
    margin: 0;

    ${({ $rotated }) => $rotated
    ? css `
                  @media (max-width: 26rem) {
                      ${compactTitleCss};
                  }
              `
    : css `
                  @media (max-height: 26rem) {
                      ${compactTitleCss};
                  }
              `}
`;
const compactSubtitleCss = css `
    font-size: 0.875rem;
    line-height: 1.25rem;
    margin: 0;
`;
const Subtitle = styled.p `
    font-size: 1rem;
    line-height: 1.5rem;
    color: rgba(235, 235, 245, 0.6);
    margin: 0 0 0.5rem;
    text-align: center;

    ${({ $rotated }) => $rotated
    ? css `
                  @media (max-width: 26rem) {
                      ${compactSubtitleCss};
                  }
              `
    : css `
                  @media (max-height: 26rem) {
                      ${compactSubtitleCss};
                  }
              `}
`;
const compactInputCss = css `
    height: 2.5rem;
    font-size: 0.875rem;
`;
const NameInput = styled(TextInput) `
    ${({ $rotated }) => $rotated
    ? css `
                  @media (max-width: 26rem) {
                      ${compactInputCss};
                  }
              `
    : css `
                  @media (max-height: 26rem) {
                      ${compactInputCss};
                  }
              `}
`;
const compactTextareaCss = css `
    height: 2.5rem;
    min-height: 2.5rem;
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
`;
const MessageInput = styled.textarea `
    background-color: var(--tv-ui-color-1);
    box-shadow:
        1px 1.5px 4px 0px #0000001a inset,
        1px 1.5px 4px 0px #00000014 inset,
        0px -0.5px 1px 0px #ffffff40 inset,
        0px -0.5px 1px 0px #ffffff4d inset;
    border: none;
    border-radius: 0.75rem;
    box-sizing: border-box;
    color: #ededf2;
    display: ${({ $rotated }) => ($rotated ? 'block' : 'none')};
    font-family: inherit;
    font-size: 1rem;
    min-height: 6rem;
    outline: none;
    padding: 0.75rem;
    width: 100%;
    font-weight: 500;
    line-height: 1.25rem;
    resize: none;

    ${({ $rotated }) => !$rotated &&
    css `
            @media (min-width: 30rem) {
                display: block;
            }
        `}

    ${({ $rotated }) => $rotated
    ? css `
                  @media (max-width: 26rem) {
                      ${compactTextareaCss};
                  }
              `
    : css `
                  @media (max-height: 26rem) {
                      ${compactTextareaCss};
                  }
              `}

    &:focus {
        box-shadow:
            0px 1px 1px rgba(255, 255, 255, 0.3),
            inset 0px 2px 6px rgba(39, 39, 39, 0.4),
            0 0 0 2px rgba(239, 239, 239, 0.3);
    }

    &::placeholder {
        color: rgba(235, 235, 245, 0.6);
    }
`;
const compactButtonCss = css `
    height: 2.5rem;
    min-height: 2.5rem;
    font-size: 0.875rem;
`;
const SendButton = styled(Button) `
    width: 100%;
    margin-top: 0.5rem;

    ${({ $rotated }) => $rotated
    ? css `
                  @media (max-width: 26rem) {
                      margin-top: 0;
                      ${compactButtonCss};
                  }
              `
    : css `
                  @media (max-height: 26rem) {
                      margin-top: 0;
                      ${compactButtonCss};
                  }
              `}
`;
const CancelButton = styled(Button) `
    width: 100%;

    ${({ $rotated }) => $rotated
    ? css `
                  @media (max-width: 26rem) {
                      ${compactButtonCss};
                  }
              `
    : css `
                  @media (max-height: 26rem) {
                      ${compactButtonCss};
                  }
              `}
`;
const requestSpeakerRolePopup = Popup.create(RequestSpeakerRolePopup);
export async function showRequestSpeakerRolePopup({ anchorRef, initialName, hostUserId, requestedRole, isCallMode, onBeforeOpen, onDismiss, }) {
    return new Promise((resolve) => {
        executeSoon(async () => {
            onBeforeOpen?.();
            try {
                const result = await requestSpeakerRolePopup.pop({
                    anchorRef,
                    initialName,
                    hostUserId,
                    requestedRole,
                    isCallMode,
                });
                resolve(result);
            }
            catch (_) {
                resolve(null);
            }
            finally {
                onDismiss?.();
            }
        });
    });
}
